import { useState } from "react";
import {
  fetchVideoSummary,
  getSubmissionApiErrorMessage,
} from "@/projects/lib/submissionsApi";
import { Sparkles, Loader2, Copy, Check } from "lucide-react";
import { Button } from "@/projects/components/ui/button";
import { toast } from "@/projects/lib/toast";

type Props = {
  studentName: string;
  videoTitle: string;
  decision: "Ready for Reward" | "Not Proper" | null;
  notes: string;
  videoUrl?: string;
  onApply: (text: string) => void;
};

export function AiFeedbackAssistant({ videoUrl, onApply }: Props) {
  const [suggestion, setSuggestion] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    if (!videoUrl) {
      toast.error("No video URL available for this submission.");
      return;
    }
    setLoading(true);
    try {
      const summary = await fetchVideoSummary(videoUrl);
      if (!summary) {
        toast.error("No summary was returned for this video.");
        return;
      }
      setSuggestion(summary);
    } catch (e) {
      toast.error(
        getSubmissionApiErrorMessage(e, "Couldn't generate suggestion.")
      );
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(suggestion);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="border-gold/40 from-royal-soft via-card to-gold-soft/30 relative overflow-hidden rounded-xl border-2 bg-gradient-to-br p-4">
      <div className="bg-gold/20 pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full blur-2xl" />
      <div className="relative flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="from-gold to-saffron shadow-soft grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-royal text-sm font-bold">
                AI Feedback Assistant
              </span>
              <span className="bg-gold text-royal rounded-full px-1.5 py-0.5 text-[9px] font-bold tracking-wider uppercase">
                New
              </span>
            </div>
            <div className="text-muted-foreground text-[11px]">
              Generate polite, constructive remarks in one click.
            </div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={run}
          disabled={loading}
          className="bg-royal hover:bg-royal/90"
        >
          {loading ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Writing…
            </>
          ) : (
            <>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />{" "}
              {suggestion ? "Regenerate" : "Suggest remarks"}
            </>
          )}
        </Button>
      </div>

      {suggestion && (
        <div className="bg-background/80 relative mt-3 rounded-lg border p-3 text-sm leading-relaxed">
          {suggestion}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => onApply(suggestion)}
              className="bg-gold text-royal hover:bg-gold/90"
            >
              Use this remark
            </Button>
            <Button size="sm" variant="outline" onClick={copy}>
              {copied ? (
                <>
                  <Check className="mr-1 h-3.5 w-3.5" /> Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
